import expandCamera from 'helpers/projects/expandCamera'
import initialPresentationValues from 'helpers/presentation/initialPresentationValues'
import updateModel from 'reducers/updateModel'
import { addProjectDefaults } from 'helpers/projects/projectDefaults'
import prepEntities from 'helpers/map/prepEntities'
import prepCurrentEntity from 'helpers/projects/prepCurrentEntity'
import defaultSettings from 'helpers/defaultSettings'
import expandEntities from 'helpers/projects/expandEntities'
import omit from 'ramda/src/omit'
import { UNIT_KEY, IMPERIAL, METRIC } from 'helpers/globals'
import localStorage from 'helpers/localStorage'
import changeCameraUnit from 'helpers/cameras/changeCameraUnit'

function expandCamerasForExport(cameras) {
  return cameras.map((camera, index) => {
    return expandCamera({ ...camera, active: index === 0 })
  })
}

function expandUnitPreference(isImperial, defaultValue, offline) {
  if (!offline && (isImperial === true || isImperial === false)) return { isImperial }
  if (localStorage.getItem(UNIT_KEY)) return { isImperial: localStorage.getItem(UNIT_KEY) === IMPERIAL, needsConversion: true }
  return { isImperial: defaultValue, needsConversion: true }
}

function addCameraModel(camera, cameraModels) {
  if (!camera.modelId) return camera
  const model = cameraModels.find(m => m.id === camera.modelId)
  // https://github.com/ipvideomarketinfo/calculator-v3/issues/317
  if (!model) return camera
  const aov = camera.modelAovId && model.aovs.find(a => a.id === camera.modelAovId)
  return updateModel(camera, { aov, model, skipClearPpfNightMode: true })
}

function expandUserSettings(action, state, project) {
  if (action.settings) return action.settings.userSettings

  return state.userSettings || (EXPORT && project.userSettings) || defaultSettings.userSettings
}

export default function expandProject(state, action) {
  const projectWithDefaults = {
    ...action,
    project: addProjectDefaults(action.project)
  }
  const {
    permalink, offline, active, project,
    project: { currentEntity, title, location, zoom, createdAt,
      updatedAt, _id, _rev, mapType, exportType, messages,
      map: { floorPlans, markups },
      manufacturerSpecificCameraModels, manufacturerSpecific, entities
    } } = projectWithDefaults

  const cameras = EXPORT ? expandCamerasForExport(project.cameras) : project.cameras.map(expandCamera)

  const camerasWithModels = cameras.map(c => addCameraModel(c, state.cameraModels))

  const { isImperial, needsConversion } = expandUnitPreference(action.project.ui.isImperial, project.ui.isImperial, offline)

  const convertedCameras = camerasWithModels.map(camera => {
    if (!needsConversion || !camera.unitOfMeasurement || camera.unitOfMeasurement === (isImperial ? IMPERIAL : METRIC)) return camera
    return changeCameraUnit({ camera, multiplier: isImperial ? 3.281 : 0.3048 })
  })

  const camera = convertedCameras.find(c => c.active === true)

  const ui = {
    ...project.ui,
    isImperial
  }

  const onlineAndPermalink = {
    projects: [
      {
        id: 1,
        title: permalink ? title : 'New Project',
        permalink: true
      },
      ...(state.projects || [])
    ],
    currentProjectMetadata: { _id: 1 }
  }

  const offlineState = title => ({
    projects: [
      {
        id: 1,
        title
      }
    ],
    currentProjectMetadata: { _id: 1 },
    recentModels: [],
    recentDoorControllerModels: []
  })

  const inactiveState = {
    ui: {
      ...ui,
      modal: 'LoginForm'
    }
  }

  return Object.assign(
    {},
    state,
    {
      currentProjectMetadata: Object.assign(
        {},
        {
          _id,
          _rev
        },
        !offline && !permalink && !EXPORT && {
          settingsId: action.settings ? action.settings._id : state.currentProjectMetadata.settingsId
        }
      ),
      presentationValues: camera && initialPresentationValues(camera),
      map: {
        ...state.map,
        zoom,
        mapType,
        location,
        // https://github.com/ipvideomarketinfo/calculator-v3/issues/301
        floorPlans: prepEntities(floorPlans),
        // https://github.com/ipvideomarketinfo/calculator-v3/issues/283
        // https://github.com/ipvideomarketinfo/calculator-v3/issues/277
        markups: prepEntities(markups)
      },
      ui,
      messages,
      createdAt,
      updatedAt,
      cameras: convertedCameras,
      currentEntity: prepCurrentEntity(currentEntity, cameras, markups, floorPlans, entities),
      offline: offline || false,
      active: active == undefined ? true : active,
      permalink: permalink || false,
      manufacturerSpecific: manufacturerSpecific && manufacturerSpecificCameraModels,
      userSettings: expandUserSettings(action, state, project),
      entities: expandEntities(entities, state.doorControllerModels, state.expansionControllerModels)
    },
    (!offline && permalink) && { ...onlineAndPermalink },
    (offline && !permalink) && { ...offlineState(title) },
    permalink && { recentModels: [], recentDoorControllerModels: [] },
    (active === false) && { ...inactiveState },
    manufacturerSpecificCameraModels && { manufacturerSpecificCameraModels },
    EXPORT && {
      floorPlansLoaded: 0,
      exportType
    },
    !EXPORT && {
      exports: []
    },
    omit(['_id', '_rev', 'type', 'userSettings'], action.settings)
  )
}



// WEBPACK FOOTER //
// ./src/reducers/expandProject.js